/**
 * Cart sparkle effect — visual confirmation that the header cart was updated.
 * Emits particles from the cart icon, pulses the icon, bumps the count badge,
 * and optionally writes a new count value into `.header-cart__count`.
 */

const SPARKLE_GLYPHS = ["✦", "✧", "★", "✶", "•"];
const PARTICLE_COUNT = 12;
const MIN_DISTANCE_PX = 22;
const MAX_DISTANCE_PX = 48;
const MAX_DELAY_MS = 140;
const SPARKLING_CLASS_MS = 520;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function buildParticle(): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = "cart-sparkle";
  el.textContent =
    SPARKLE_GLYPHS[Math.floor(Math.random() * SPARKLE_GLYPHS.length)];

  const angle = Math.random() * Math.PI * 2;
  const distance =
    MIN_DISTANCE_PX + Math.random() * (MAX_DISTANCE_PX - MIN_DISTANCE_PX);
  const dx = Math.cos(angle) * distance;
  const dy = Math.sin(angle) * distance;
  const delay = Math.random() * MAX_DELAY_MS;
  const rotate = (Math.random() * 360 - 180).toFixed(0);
  const scale = (0.7 + Math.random() * 0.6).toFixed(2);

  el.style.setProperty("--sparkle-dx", `${dx.toFixed(1)}px`);
  el.style.setProperty("--sparkle-dy", `${dy.toFixed(1)}px`);
  el.style.setProperty("--sparkle-delay", `${delay.toFixed(0)}ms`);
  el.style.setProperty("--sparkle-rotate", `${rotate}deg`);
  el.style.setProperty("--sparkle-scale", scale);

  el.addEventListener(
    "animationend",
    () => {
      el.remove();
    },
    { once: true },
  );

  return el;
}

/**
 * Trigger the sparkle burst on the header cart icon.
 *
 * @param newCount  If provided, replaces the `.header-cart__count` text so the
 *                  badge stays in sync with the fresh cart state.
 */
export function triggerCartSparkle(newCount?: number): void {
  const $cart = $(".header-cart");
  if ($cart.length === 0) return;

  if (typeof newCount === "number" && Number.isFinite(newCount)) {
    $cart.find(".header-cart__count").text(String(newCount));
  }

  if (prefersReducedMotion()) {
    return;
  }

  $cart.each(function () {
    const cart = this;
    cart.classList.add("is-sparkling");
    window.setTimeout(() => {
      cart.classList.remove("is-sparkling");
    }, SPARKLING_CLASS_MS);

    const frag = document.createDocumentFragment();
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      frag.appendChild(buildParticle());
    }
    cart.appendChild(frag);
  });
}
